/**
 * BAS (Biological Assessment System) API client.
 * Communicates with the BAS API for parameter tracking and related data.
 */

import axios from 'axios';
import { useCallback } from 'react';
import { getEnvVar } from '../utils/env';
import { withAuth } from './auth';

export const BAS_API_URL =
  getEnvVar('REACT_APP_BAS_API_URL') || 'https://bas-api-prod.onrender.com';

const basApi = axios.create({
  baseURL: BAS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

basApi.interceptors.request.use(
  (config) => config,
  (error) => {
    console.error('BAS API Request Error:', error);
    return Promise.reject(error);
  }
);

basApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(
      `BAS API Error: ${error.response?.status || 'No Status'} from ${error.config?.url || 'Unknown URL'}`
    );
    console.error('BAS API Error details:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * Hook that provides BAS API functions with authentication.
 * Must be called with apiCall from useApi (or equivalent).
 *
 * @param {Function} apiCall - The shared apiCall wrapper that handles auth and error redirects
 * @returns {Object} BAS API functions
 */
export function useBasApi(apiCall) {
  const getBasParameterTracking = useCallback(
    async (parameterName, stockName) => {
      return await apiCall(async (token) => {
        return await basApi.post(
          '/parameter-data',
          { parameter_name: parameterName },
          { headers: withAuth({}, token) }
        );
      });
    },
    [apiCall]
  );

  const getBasParameterById = useCallback(
    async (parameterId) => {
      return await apiCall(async (token) => {
        return await basApi.get(`/parameter-by-id/${parameterId}`, {
          headers: withAuth({}, token),
        });
      });
    },
    [apiCall]
  );

  return {
    getBasParameterTracking,
    getBasParameterById,
  };
}

/**
 * Legacy standalone export for backward compatibility.
 * Prefer useApi().getBasParameterTracking when using React components.
 */
export const getBasParameterTracking = async (
  parameterName,
  stockName,
  accessToken
) => {
  return await basApi.post(
    '/parameter-data',
    { parameter_name: parameterName },
    { headers: withAuth({}, accessToken) }
  );
};

/**
 * Legacy standalone export for backward compatibility.
 * Prefer useApi().getBasParameterById when using React components.
 */
export const getBasParameterById = async (parameterId, accessToken) => {
  return await basApi.get(`/parameter-by-id/${parameterId}`, {
    headers: withAuth({}, accessToken),
  });
};
